import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Sparkles, ArrowRight, X, Clock } from "lucide-react";
import { MAIN_BG_GRADIENT } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';

export default function BetaAccessPage() {
  const navigate = useNavigate();
  const { setBetaAccessVerified } = useAuth();
  const [accessCode, setAccessCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 47, seconds: 32 });

  const CORRECT_CODE = "YC2025TRIAL";

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Show the code input after the title animation
    const timer = setTimeout(() => {
      setShowCodeInput(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const glow = document.getElementById("mouse-glow");
    const update = (e) => {
      glow.style.transform = `translate(${e.clientX - 150}px, ${e.clientY - 150}px)`;
    };
    document.addEventListener("mousemove", update);
    return () => document.removeEventListener("mousemove", update);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    await new Promise(resolve => setTimeout(resolve, 500));
    if (accessCode.trim().toUpperCase() === CORRECT_CODE) {
      localStorage.setItem('betaAccessVerified', 'true');
      setBetaAccessVerified(true);
      navigate("/");
    } else {
      setError("Invalid beta access code. Please try again.");
    }
    setIsSubmitting(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('betaAccessVerified');
    setBetaAccessVerified(false);
    navigate("/auth");
  };

  // Card grow animation when code input appears
  const cardVariants = {
    initial: { scale: 1 },
    expanded: { scale: 1.08 },
  };

  return (
    <div className={`${isDark ? 'dark' : ''} relative min-h-screen transition-colors duration-300 ${MAIN_BG_GRADIENT}`}>
      <div className="flex flex-col min-h-screen overflow-hidden">
        {/* Mouse glow effect */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div id="mouse-glow" className="absolute w-72 h-72 bg-white/30 rounded-full blur-3xl -z-10 transition-transform"></div>
        </div>
                 {/* Floating particles */}
         <div className="absolute inset-0 z-0 pointer-events-none">
           {[...Array(6)].map((_, i) => (
             <motion.div
               key={i}
               className="absolute w-2 h-2 bg-orange-400/30 rounded-full"
               style={{ left: `${20 + i * 15}%`, top: `${30 + i * 10}%` }}
               animate={{ y: [0, -20, 0], opacity: [0.3, 0.8, 0.3] }}
               transition={{ duration: 3 + i * 0.5, repeat: Infinity, delay: i * 0.5 }}
             />
           ))}
         </div>
        {/* Logout button */}
        <div className="absolute top-5 right-5 z-50">
          <button
            onClick={handleLogout}
            className="p-2 rounded-full bg-white/20 dark:bg-gray-800/20 backdrop-blur-md shadow-md hover:bg-white/30 dark:hover:bg-gray-800/30 transition-all duration-300"
          >
            <X className="text-gray-700 dark:text-gray-300 w-5 h-5" />
          </button>
        </div>
        <main className="flex-1 flex items-center justify-center py-12 px-4 z-10">
          <div className="max-w-4xl w-full">
                         <motion.div
               variants={cardVariants}
               initial="initial"
               animate={showCodeInput ? "expanded" : "initial"}
               transition={{ type: 'spring', stiffness: 120, damping: 12, duration: 0.6 }}
               className="bg-white/60 dark:bg-white/10 rounded-3xl p-8 backdrop-blur-md border border-gray-200 dark:border-gray-700"
             >
              <div className="text-center mb-8">
                {/* Logo */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="mb-6"
                >
                  <img
                    src="/logo.png"
                    alt="Quis Lab Logo"
                    className="mx-auto h-16 w-16 animate-[wiggle_1.5s_ease-in-out_infinite]"
                  />
                </motion.div>
                
                {/* Title with animation */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="mb-0"
                >
                 <div className="relative inline-block mb-2">
                 <div className="relative inline-block mb-2">
  {/* Quis heading */}
  <h1 className="text-5xl font-extrabold text-gray-800 dark:text-white tracking-tight">
    Quis
  </h1>

  {/* Beta Access label */}
  {/* <div className="absolute -top-0 -right-8 px-2 py-0.5 rounded-full border border-black text-black text-[9px] font-medium tracking-wide bg-transparent">
    Beta
  </div> */}
</div>

</div>

                </motion.div>
            {/* Beta Access Tag under Logo */}
<motion.div
  initial={{ opacity: 0, scale: 0.95 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.6, delay: 1.0 }}
  className="mb-6"
>
  <div className="inline-block px-3 py-1 border border-black rounded-full text-xs font-medium text-black bg-transparent">
    Beta Access
  </div>
</motion.div>

                {/* Countdown timer */}
                {/* <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                  className="mb-6"
                >
                                    <div className="inline-flex items-center gap-2 bg-purple-100 dark:bg-purple-900/30 px-4 py-2 rounded-full shadow-sm">
                 <Clock className="w-4 h-4 text-purple-600 dark:text-purple-300" />
                 <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
                 Beta Access ends in: {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
                 </span>
                </div>

                </motion.div> */}
                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  className="text-gray-600 dark:text-gray-300 text-base leading-relaxed max-w-2xl mx-auto"
                >
                  You're one step away from joining the future of professional networking. 
                  Enter your exclusive beta access code to unlock access the future, join our curated community now.
                </motion.p>
              </div>
              {/* Code input form */}
              <AnimatePresence>
                {showCodeInput && (
                  <motion.form
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    onSubmit={handleSubmit}
                    className="space-y-6 max-w-md mx-auto"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <label htmlFor="access-code" className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2 justify-center">
                        <Lock className="w-4 h-4" />
                        Beta Access Code
                      </label>
                      <motion.div 
                        className="relative mt-2"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                                                 <input
                           id="access-code"
                           type="text"
                           placeholder="Enter your beta access code"
                           className="w-full py-4 px-6 rounded-xl border-2 border-black dark:border-gray-600 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-300 text-center font-mono text-xl tracking-wider font-bold"
                           value={accessCode}
                           onChange={(e) => setAccessCode(e.target.value)}
                           autoComplete="off"
                           autoFocus
                         />
                                                 <div className="absolute inset-0 rounded-xl border-2 border-transparent bg-gradient-to-r from-orange-500/20 to-purple-500/20 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                      </motion.div>
                    </motion.div>
                    {/* Error message */}
                    <AnimatePresence>
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="text-red-500 text-sm text-center bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800"
                        >
                          {error}
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {/* Submit button */}
                                         <motion.button
                       type="submit"
                       disabled={isSubmitting || !accessCode.trim()}
                       className="w-full flex items-center justify-center bg-black hover:bg-gray-800 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group text-lg"
                       initial={{ opacity: 0, y: 20 }}
                       animate={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.6, delay: 0.6 }}
                       whileHover={{ scale: 1.02 }}
                       whileTap={{ scale: 0.98 }}
                     >
                      {isSubmitting ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Verifying Beta Access...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>Unlock Beta Access</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </div>
                      )}
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </main>
        <footer className="text-center py-6 text-sm text-gray-400 dark:text-gray-500 z-10">
          <p>&copy; 2025 Quis Lab. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}